/**
 * Copyright © 2022-2024 Delicious AI, LLC
 *
 * @author Kyle Armstrong <kyle.armstrong@deliciousai.com>
 */

import { Box, SxProps } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { TextFieldProps } from '@mui/material/TextField'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import * as React from 'react'

type MUIDatePickerPickerProps = {
  date: Dayjs | null
  onDateChange: (date: dayjs.Dayjs | null) => void
  label: string
  variant?: TextFieldProps['variant']
  textProps?: TextFieldProps
  open?: boolean
  error?: boolean
  helperText?: string
  sx?: SxProps<Theme>
  hidden?: boolean
  minDate?: Dayjs
  maxDate?: Dayjs
  clearable?: boolean
}
export const MUIDatePicker = (props: MUIDatePickerPickerProps) => {
  const {
    date,
    onDateChange,
    label,
    variant = 'outlined',
    textProps = {},
    open,
    error,
    helperText,
    sx,
    hidden,
    minDate,
    maxDate,
    clearable = false,
  } = props

  if (hidden) return null
  const Body = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={date}
        open={open}
        minDate={minDate}
        maxDate={maxDate}
        onChange={newValue => {
          onDateChange(newValue)
        }}
        slotProps={{
          ...(clearable
            ? { field: { clearable: true, onClear: () => onDateChange(null) } }
            : {}),
          textField: {
            error: !!error,
            helperText,
            variant,
            fullWidth: true,
            ...textProps,
          },
          desktopPaper: {
            variant: 'outlined',
            elevation: 0,
          },
        }}
        sx={{ color: 'red' }}
      />
    </LocalizationProvider>
  )
  if (sx) {
    return <Box sx={sx}>{Body}</Box>
  }
  return Body
}
